import axios from 'axios';
import { getToken } from './tokenStorage';
import { handleAuthFailure } from './authFailure';
import { getErrorMessage } from "./errorHandling";
import { refreshAuthToken } from './tokenRefresh';

const sendRequest = async (path, options = {}, authToken = null) => {
  const token = authToken || getToken();
  const server_host = process.env.REACT_APP_SERVER_HOST;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${server_host}${normalizedPath}`;

  // Destructure options to separate what we handle explicitly
  const { body, data, headers: optionHeaders, ...restOfOptions } = options;

  // Prepare headers, starting with a copy of headers from options
  const headers = { ...optionHeaders };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Prepare request data - prioritize 'data' over 'body' for backward compatibility
  let requestData = data !== undefined ? data : body;

  if (requestData) {
    if (requestData instanceof FormData) {
      // For FormData, let the browser set the Content-Type.
      delete headers['Content-Type'];
    } else if (headers['Content-Type'] === 'application/json' && typeof requestData === 'object') {
      // For JSON, stringify the data.
      requestData = JSON.stringify(requestData);
    }
  }

  // Prepare the axios request config
  const axiosRequest = {
    url,
    ...restOfOptions,
    headers,
    data: requestData,
  };

  return axios(axiosRequest);
};

const authRequest = async (path, options = {}, authToken = null) => {
  try {
    return await sendRequest(path, options, authToken);
  } catch (error) {
    if (error.isAxiosError && error.response && error.response.status === 401) {
      // Attempt to refresh token
      const newToken = await refreshAuthToken();
      if (newToken) {
        return await sendRequest(path, options, newToken);
      }
      // If refresh fails or no refresh token, handle auth failure
      await handleAuthFailure(error);
      // After handling failure, reject with a standard error
      return Promise.reject(new Error(getErrorMessage(error)));
    } else if (error.isAxiosError && error.message === "Network Error") {
      error.isNetworkError = true;
      throw error;
    } else {
      throw error;
    }
  }
};

export default authRequest;
